import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Generates and downloads a PDF performance report for a test run.
 *
 * @param {Object} testRun - The test run data object
 * @param {Object|null} report - The report data object (metrics, suggestions, etc.)
 */
export function exportPerformanceReportPdf(testRun, report) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  let y = 20;

  const getScoreLabel = (score) => {
    if (score >= 90) return 'Excelente';
    if (score >= 70) return 'Bom';
    if (score >= 50) return 'Regular';
    return 'Precisa Melhorar';
  };

  const getScoreRgb = (score) => {
    if (score >= 90) return [76, 175, 80];   // green
    if (score >= 70) return [33, 150, 243];   // blue
    if (score >= 50) return [255, 152, 0];    // orange
    return [244, 67, 54];                      // red
  };

  const addPageIfNeeded = (requiredSpace = 30) => {
    if (y + requiredSpace > doc.internal.pageSize.getHeight() - 20) {
      doc.addPage();
      y = 20;
    }
  };

  // ── Header ──────────────────────────────────────────────────────────
  doc.setFillColor(25, 118, 210);
  doc.rect(0, 0, pageWidth, 40, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('Relatório de Performance', margin, 18);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(testRun.name || 'Teste', margin, 28);

  const dateStr = testRun.completed_at
    ? new Date(testRun.completed_at).toLocaleString('pt-BR')
    : new Date().toLocaleString('pt-BR');
  doc.setFontSize(10);
  doc.text(`Gerado em: ${dateStr}`, margin, 36);

  y = 50;

  // ── Overall Score ───────────────────────────────────────────────────
  if (testRun.overall_score !== null && testRun.overall_score !== undefined) {
    const scoreColor = getScoreRgb(testRun.overall_score);
    const scoreLabel = getScoreLabel(testRun.overall_score);

    doc.setFillColor(...scoreColor);
    doc.roundedRect(margin, y, pageWidth - margin * 2, 28, 3, 3, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(`Score Geral: ${testRun.overall_score.toFixed(1)}%`, margin + 8, y + 12);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text(`Avaliação: ${scoreLabel}`, margin + 8, y + 22);

    y += 36;
  }

  // ── Executive Summary ───────────────────────────────────────────────
  if (testRun.report?.executive_summary) {
    addPageIfNeeded(40);
    doc.setTextColor(25, 118, 210);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Resumo Executivo', margin, y);
    y += 8;

    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const summaryLines = doc.splitTextToSize(
      testRun.report.executive_summary,
      pageWidth - margin * 2
    );
    doc.text(summaryLines, margin, y);
    y += summaryLines.length * 5 + 8;
  }

  // ── Metrics Table ───────────────────────────────────────────────────
  if (report?.metrics) {
    addPageIfNeeded(50);
    doc.setTextColor(25, 118, 210);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Métricas por Dimensão', margin, y);
    y += 4;

    const metricsData = [
      ['Assertividade', report.metrics.assertiveness?.average?.toFixed(1) || '0'],
      ['Coerência', report.metrics.coherence?.average?.toFixed(1) || '0'],
      ['Relevância', report.metrics.relevance?.average?.toFixed(1) || '0'],
      ['Completude', report.metrics.completeness?.average?.toFixed(1) || '0'],
    ];

    autoTable(doc, {
      startY: y,
      head: [['Dimensão', 'Score Médio']],
      body: metricsData,
      margin: { left: margin, right: margin },
      headStyles: {
        fillColor: [25, 118, 210],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      alternateRowStyles: { fillColor: [240, 245, 255] },
      styles: { fontSize: 10 },
    });

    y = doc.lastAutoTable.finalY + 10;
  }

  // ── Score Distribution ──────────────────────────────────────────────
  if (report?.score_distribution) {
    addPageIfNeeded(50);
    doc.setTextColor(25, 118, 210);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Distribuição de Scores', margin, y);
    y += 4;

    const distData = Object.entries(report.score_distribution).map(([name, value]) => [
      name,
      String(value),
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Faixa', 'Quantidade']],
      body: distData,
      margin: { left: margin, right: margin },
      headStyles: {
        fillColor: [25, 118, 210],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      alternateRowStyles: { fillColor: [240, 245, 255] },
      styles: { fontSize: 10 },
    });

    y = doc.lastAutoTable.finalY + 10;
  }

  // ── Strengths ───────────────────────────────────────────────────────
  if (testRun.report?.strengths?.length > 0) {
    addPageIfNeeded(40);
    doc.setTextColor(76, 175, 80);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Pontos Fortes', margin, y);
    y += 8;

    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    testRun.report.strengths.forEach((strength) => {
      addPageIfNeeded(10);
      const lines = doc.splitTextToSize(`• ${strength}`, pageWidth - margin * 2 - 4);
      doc.text(lines, margin + 2, y);
      y += lines.length * 5 + 3;
    });
    y += 5;
  }

  // ── Areas for Improvement ───────────────────────────────────────────
  if (testRun.report?.areas_for_improvement?.length > 0) {
    addPageIfNeeded(40);
    doc.setTextColor(255, 152, 0);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Áreas para Melhoria', margin, y);
    y += 8;

    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    testRun.report.areas_for_improvement.forEach((area) => {
      addPageIfNeeded(14);
      const areaText = area.area || area;
      const descText = area.description ? ` – ${area.description}` : '';
      const lines = doc.splitTextToSize(`• ${areaText}${descText}`, pageWidth - margin * 2 - 4);
      doc.text(lines, margin + 2, y);
      y += lines.length * 5 + 3;
    });
    y += 5;
  }

  // ── Detailed Results Table ──────────────────────────────────────────
  if (testRun.results?.length > 0) {
    addPageIfNeeded(50);
    doc.setTextColor(25, 118, 210);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Resultados Detalhados', margin, y);
    y += 4;

    const resultsData = testRun.results.map((r, idx) => [
      String(idx + 1),
      (r.question_text || '').substring(0, 60) + ((r.question_text || '').length > 60 ? '...' : ''),
      r.overall_score?.toFixed(0) || '0',
      r.assertiveness_score?.toFixed(0) || '0',
      r.coherence_score?.toFixed(0) || '0',
      r.relevance_score?.toFixed(0) || '0',
      r.completeness_score?.toFixed(0) || '0',
      r.response_time_ms ? `${r.response_time_ms}ms` : '-',
    ]);

    autoTable(doc, {
      startY: y,
      head: [['#', 'Pergunta', 'Score', 'Assert.', 'Coer.', 'Relev.', 'Compl.', 'Tempo']],
      body: resultsData,
      margin: { left: margin, right: margin },
      headStyles: {
        fillColor: [25, 118, 210],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
      },
      alternateRowStyles: { fillColor: [240, 245, 255] },
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 8 },
        1: { cellWidth: 55 },
        2: { cellWidth: 14, halign: 'center' },
        3: { cellWidth: 14, halign: 'center' },
        4: { cellWidth: 14, halign: 'center' },
        5: { cellWidth: 14, halign: 'center' },
        6: { cellWidth: 14, halign: 'center' },
        7: { cellWidth: 18, halign: 'center' },
      },
    });

    y = doc.lastAutoTable.finalY + 10;
  }

  // ── Detailed Q&A per result ─────────────────────────────────────────
  if (testRun.results?.length > 0) {
    addPageIfNeeded(30);
    doc.setTextColor(25, 118, 210);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Detalhamento por Pergunta', margin, y);
    y += 8;

    testRun.results.forEach((result, idx) => {
      addPageIfNeeded(50);

      // Question header
      const scoreColor = getScoreRgb(result.overall_score || 0);
      doc.setFillColor(...scoreColor);
      doc.roundedRect(margin, y, pageWidth - margin * 2, 8, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(
        `Pergunta ${idx + 1} — Score: ${result.overall_score?.toFixed(0) || 0}`,
        margin + 4,
        y + 5.5
      );
      y += 12;

      // Question text
      doc.setTextColor(60, 60, 60);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Pergunta:', margin, y);
      y += 4;
      doc.setFont('helvetica', 'normal');
      const qLines = doc.splitTextToSize(result.question_text || '', pageWidth - margin * 2 - 4);
      doc.text(qLines, margin + 2, y);
      y += qLines.length * 4 + 4;

      // Expected answer
      addPageIfNeeded(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Resposta Esperada:', margin, y);
      y += 4;
      doc.setFont('helvetica', 'normal');
      const eLines = doc.splitTextToSize(
        result.expected_answer || 'Não definida',
        pageWidth - margin * 2 - 4
      );
      doc.text(eLines, margin + 2, y);
      y += eLines.length * 4 + 4;

      // Agent response
      addPageIfNeeded(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Resposta do Agente:', margin, y);
      y += 4;
      doc.setFont('helvetica', 'normal');
      const aLines = doc.splitTextToSize(
        result.agent_response || 'Sem resposta',
        pageWidth - margin * 2 - 4
      );
      doc.text(aLines, margin + 2, y);
      y += aLines.length * 4 + 4;

      // Feedback
      if (result.feedback) {
        addPageIfNeeded(20);
        doc.setFont('helvetica', 'bold');
        doc.text('Feedback:', margin, y);
        y += 4;
        doc.setFont('helvetica', 'normal');
        const fLines = doc.splitTextToSize(result.feedback, pageWidth - margin * 2 - 4);
        doc.text(fLines, margin + 2, y);
        y += fLines.length * 4 + 4;
      }

      y += 6;
    });
  }

  // ── Recommendations ─────────────────────────────────────────────────
  if (testRun.report?.recommendations?.length > 0) {
    addPageIfNeeded(40);
    doc.setTextColor(25, 118, 210);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Recomendações de Melhoria', margin, y);
    y += 8;

    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    testRun.report.recommendations.forEach((rec, idx) => {
      addPageIfNeeded(18);
      const title = rec.title || `Recomendação ${idx + 1}`;
      const impact = rec.expected_impact ? ` [Impacto: ${rec.expected_impact}]` : '';
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(`${idx + 1}. ${title}${impact}`, pageWidth - margin * 2);
      doc.text(titleLines, margin, y);
      y += titleLines.length * 5 + 2;

      doc.setFont('helvetica', 'normal');
      const desc = rec.description || (typeof rec === 'string' ? rec : '');
      if (desc) {
        const descLines = doc.splitTextToSize(desc, pageWidth - margin * 2 - 4);
        doc.text(descLines, margin + 4, y);
        y += descLines.length * 5 + 4;
      }
    });
    y += 5;
  }

  // ── Improvement Suggestions ─────────────────────────────────────────
  if (report?.improvement_suggestions?.length > 0) {
    addPageIfNeeded(40);
    doc.setTextColor(25, 118, 210);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Ações Sugeridas', margin, y);
    y += 8;

    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    report.improvement_suggestions.forEach((suggestion) => {
      addPageIfNeeded(10);
      const lines = doc.splitTextToSize(`• ${suggestion}`, pageWidth - margin * 2 - 4);
      doc.text(lines, margin + 2, y);
      y += lines.length * 5 + 3;
    });
    y += 5;
  }

  // ── Conclusion ──────────────────────────────────────────────────────
  if (testRun.report?.conclusion) {
    addPageIfNeeded(30);
    doc.setTextColor(25, 118, 210);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Conclusão', margin, y);
    y += 8;

    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const conclusionLines = doc.splitTextToSize(
      testRun.report.conclusion,
      pageWidth - margin * 2
    );
    doc.text(conclusionLines, margin, y);
    y += conclusionLines.length * 5 + 8;
  }

  // ── Footer on every page ────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `JBT Eval — Relatório de Performance — Página ${i} de ${totalPages}`,
      pageWidth / 2,
      pageHeight - 8,
      { align: 'center' }
    );
  }

  // ── Save ────────────────────────────────────────────────────────────
  const safeName = (testRun.name || 'relatorio').replace(/[^a-zA-Z0-9_-]/g, '_');
  doc.save(`${safeName}_performance_report.pdf`);
}


/**
 * Generates and downloads a PDF performance report for an agent's test history.
 *
 * @param {Object} agent - The agent data object
 * @param {Object} testHistory - The test history data (summary + history array)
 * @param {string} filterLabel - Description of the active filter (e.g. test names or "Todos os testes")
 */
export function exportAgentPerformancePdf(agent, testHistory, filterLabel) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  let y = 20;

  const addPageIfNeeded = (requiredSpace = 30) => {
    if (y + requiredSpace > doc.internal.pageSize.getHeight() - 20) {
      doc.addPage();
      y = 20;
    }
  };

  // ── Header ──────────────────────────────────────────────────────────
  doc.setFillColor(25, 118, 210);
  doc.rect(0, 0, pageWidth, 44, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('Dashboard de Performance', margin, 18);

  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.text(`Agente: ${agent.name || 'N/A'}`, margin, 28);

  doc.setFontSize(10);
  doc.text(`Filtro: ${filterLabel}`, margin, 36);
  doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, margin, 42);

  y = 54;

  // ── Summary ─────────────────────────────────────────────────────────
  if (testHistory?.summary) {
    const s = testHistory.summary;

    doc.setTextColor(25, 118, 210);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Resumo', margin, y);
    y += 4;

    const summaryData = [
      ['Total de Testes', String(s.total_test_runs)],
      ['Score Mais Recente', `${s.latest_score.toFixed(1)}%`],
      ['Melhor Score', `${s.best_score.toFixed(1)}%`],
      ['Pior Score', `${s.worst_score.toFixed(1)}%`],
      ['Score Médio', `${s.average_score.toFixed(1)}%`],
      ['Tendência', s.score_trend === 'improving' ? 'Melhorando' : s.score_trend === 'declining' ? 'Piorando' : s.score_trend === 'stable' ? 'Estável' : 'Sem dados'],
    ];

    autoTable(doc, {
      startY: y,
      head: [['Indicador', 'Valor']],
      body: summaryData,
      margin: { left: margin, right: margin },
      headStyles: {
        fillColor: [25, 118, 210],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      alternateRowStyles: { fillColor: [240, 245, 255] },
      styles: { fontSize: 10 },
    });

    y = doc.lastAutoTable.finalY + 10;
  }

  // ── Test History Table ──────────────────────────────────────────────
  if (testHistory?.history?.length > 0) {
    addPageIfNeeded(50);
    doc.setTextColor(25, 118, 210);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Histórico de Testes', margin, y);
    y += 4;

    const historyData = testHistory.history.map((h) => [
      h.test_run_name || '-',
      h.completed_at ? new Date(h.completed_at).toLocaleDateString('pt-BR') : '-',
      String(h.total_questions),
      `${h.overall_score.toFixed(1)}%`,
      `${h.metrics.assertiveness.toFixed(1)}%`,
      `${h.metrics.coherence.toFixed(1)}%`,
      `${h.metrics.relevance.toFixed(1)}%`,
      `${h.metrics.completeness.toFixed(1)}%`,
      `${h.avg_response_time_ms.toFixed(0)} ms`,
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Teste', 'Data', 'Perguntas', 'Score', 'Assert.', 'Coer.', 'Relev.', 'Compl.', 'Tempo']],
      body: historyData,
      margin: { left: margin, right: margin },
      headStyles: {
        fillColor: [25, 118, 210],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
      },
      alternateRowStyles: { fillColor: [240, 245, 255] },
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 20 },
        2: { cellWidth: 16, halign: 'center' },
        3: { cellWidth: 16, halign: 'center' },
        4: { cellWidth: 16, halign: 'center' },
        5: { cellWidth: 16, halign: 'center' },
        6: { cellWidth: 16, halign: 'center' },
        7: { cellWidth: 16, halign: 'center' },
        8: { cellWidth: 18, halign: 'center' },
      },
    });

    y = doc.lastAutoTable.finalY + 10;
  }

  // ── Average Metrics ─────────────────────────────────────────────────
  if (testHistory?.history?.length > 0) {
    addPageIfNeeded(50);
    doc.setTextColor(25, 118, 210);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Métricas Médias', margin, y);
    y += 4;

    const history = testHistory.history;
    const avgAssert = history.reduce((s, h) => s + h.metrics.assertiveness, 0) / history.length;
    const avgCoherence = history.reduce((s, h) => s + h.metrics.coherence, 0) / history.length;
    const avgRelevance = history.reduce((s, h) => s + h.metrics.relevance, 0) / history.length;
    const avgCompleteness = history.reduce((s, h) => s + h.metrics.completeness, 0) / history.length;
    const avgOverall = history.reduce((s, h) => s + h.overall_score, 0) / history.length;
    const avgResponseTime = history.reduce((s, h) => s + h.avg_response_time_ms, 0) / history.length;

    const metricsData = [
      ['Score Geral', `${avgOverall.toFixed(1)}%`],
      ['Assertividade', `${avgAssert.toFixed(1)}%`],
      ['Coerência', `${avgCoherence.toFixed(1)}%`],
      ['Relevância', `${avgRelevance.toFixed(1)}%`],
      ['Completude', `${avgCompleteness.toFixed(1)}%`],
      ['Tempo Médio de Resposta', `${avgResponseTime.toFixed(0)} ms`],
    ];

    autoTable(doc, {
      startY: y,
      head: [['Métrica', 'Valor Médio']],
      body: metricsData,
      margin: { left: margin, right: margin },
      headStyles: {
        fillColor: [25, 118, 210],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      alternateRowStyles: { fillColor: [240, 245, 255] },
      styles: { fontSize: 10 },
    });

    y = doc.lastAutoTable.finalY + 10;
  }

  // ── Score Evolution (text summary) ──────────────────────────────────
  if (testHistory?.history?.length > 1) {
    addPageIfNeeded(40);
    doc.setTextColor(25, 118, 210);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Evolução do Score', margin, y);
    y += 8;

    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    testHistory.history.forEach((h, idx) => {
      addPageIfNeeded(8);
      const date = h.completed_at ? new Date(h.completed_at).toLocaleDateString('pt-BR') : '-';
      const line = `${idx + 1}. ${h.test_run_name} (${date}) — Score: ${h.overall_score.toFixed(1)}%`;
      doc.text(line, margin + 2, y);
      y += 6;
    });

    y += 5;
  }

  // ── Footer on every page ────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `JBT Eval — Dashboard de Performance — ${agent.name} — Página ${i} de ${totalPages}`,
      pageWidth / 2,
      pageHeight - 8,
      { align: 'center' }
    );
  }

  // ── Save ────────────────────────────────────────────────────────────
  const safeName = (agent.name || 'agente').replace(/[^a-zA-Z0-9_-]/g, '_');
  doc.save(`${safeName}_dashboard_performance.pdf`);
}
